import { useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Box } from '@mui/material'
import { get, groupBy, round, sumBy, uniq } from 'lodash'

import { FeatureMessage } from '@/components/common/UpgradeMessage'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_INTERVAL_STATS,
  GetPosIntervalStatsQuery,
  GetPosIntervalStatsQueryVariables,
  GroupByDate,
  POSAmountType,
  POSGroupBy,
} from '@/graphql'
import useT from '@/i18n/useT'
import DAIOverTimeLineChart, {
  DAIOverTimeLineChartSeries,
} from '@/pages/charts/ApexCharts/DAIOverTimeLineChart'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DateHelpers } from '@/utils/date.helpers'

type Props = {
  startDate: string
  endDate: string
  storeIds?: string[]
  categories?: string[]
  annotationStartDate?: string
  annotationEndDate?: string
  needToSetFuelSalesCategories: boolean
}

export const FuelVolumeChart: React.FC<Props> = props => {
  const {
    storeIds,
    startDate,
    endDate,
    categories,
    annotationStartDate,
    annotationEndDate,
    needToSetFuelSalesCategories,
  } = props
  const { t } = useT()

  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const { data, refetch, loading } = useQuery<
    GetPosIntervalStatsQuery,
    GetPosIntervalStatsQueryVariables
  >(GET_POS_INTERVAL_STATS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        storeIds,
        groupBy: POSGroupBy.CATEGORY,
        amountType: POSAmountType.QUANTITY,
        startDate,
        endDate,
        categories,
      },
    },
  })

  useRefresh(refetch)

  const getLabels = (): string[] => {
    const filtered = (data?.posIntervalStats.results
      ?.filter(stat => !!stat.date && !!stat.label)
      .map(s => s.date) || []) as string[]
    return uniq(filtered)
  }

  const getSeries = (): DAIOverTimeLineChartSeries[] => {
    const stats = data?.posIntervalStats.results || []
    const statsCleaned = stats
      .filter(s => !!s.label)
      .map(stat => ({
        ...stat,
        value: round(stat.value || 0),
      }))

    const grouped = groupBy(statsCleaned, 'label')
    const labels = getLabels()

    const totals = labels.map(date => {
      const allOnDate = Object.values(grouped).flatMap(group =>
        group.filter(stat => stat.date === date),
      )
      return round(sumBy(allOnDate, 'value'))
    })

    const series: DAIOverTimeLineChartSeries[] = Object.entries(grouped).map(
      ([key, value]) => ({
        name: key,
        id: key,
        dataType: 'number',
        data: value.map(stat => get(stat, 'value', 0)),
      }),
    )

    return [
      ...series,
      { id: 'total', name: 'Total', data: totals, dataType: 'number' },
    ]
  }

  return (
    <DAIOverTimeLineChart
      dataGranularity={data?.posIntervalStats?.period || GroupByDate.DAY}
      title={t('revenueDetails.fuelVolume')}
      series={getSeries()}
      dates={getLabels().map(date =>
        DateHelpers.parseIsoDateWithoutTimezone(date),
      )}
      options={{
        stroke: { curve: 'smooth' },
        yaxis: {
          labels: {
            formatter: (val: number) => (val ? val.toLocaleString() : 'N/A'),
          },
        },
        annotations: {
          xaxis: ChartHelpers.getXAxisRangeAnnotation(
            t,
            annotationStartDate,
            annotationEndDate,
            data?.posIntervalStats?.period || GroupByDate.DAY,
          ),
        },
      }}
      loading={loading}
      Overlay={
        needToSetFuelSalesCategories && (
          <Box
            sx={{
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            <FeatureMessage
              message={t('setUpCategories.message')}
              buttonText={t('setUpCategories.button')}
              onClick={() => handleNavTo('/admin/sales-data')}
            />
          </Box>
        )
      }
    />
  )
}
